import { useEffect, useState } from "react";
import { CheckCircle2, CreditCard, RefreshCw, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { Button } from "./ui/button";

export const StripeCatalogCard = () => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () =>
    api
      .get("/admin/stripe/status")
      .then(({ data }) => setStatus(data))
      .catch((e) => toast.error(errMsg(e)));

  useEffect(() => {
    load();
  }, []);

  const setup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/stripe/setup-catalog");
      toast.success(
        `Stripe ${data.mode} catalog synced: ` +
          data.results.map((r) => `${r.name} $${r.amount} (${r.action})`).join(", ")
      );
      await load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm"
      data-testid="stripe-catalog-card"
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="flex items-center gap-2 font-display text-base font-bold text-slate-900">
            <CreditCard className="h-4 w-4 text-blue-600" /> Stripe billing catalog
          </p>
          <p className="mt-1 text-sm text-slate-600">
            Creates the Starter ($15/mo), Small Business ($49/mo) and Professional ($99/mo) prices
            in the Stripe account these keys belong to. Safe to run again — existing prices are
            reused.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            data-testid="stripe-catalog-refresh"
            className="rounded-full border-slate-300"
          >
            <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Refresh
          </Button>
          <Button
            onClick={setup}
            disabled={busy}
            data-testid="stripe-catalog-setup-button"
            className="h-9 rounded-full bg-blue-600 px-5 font-semibold hover:bg-blue-700"
          >
            {busy ? "Syncing…" : "Create / sync prices"}
          </Button>
        </div>
      </div>

      {status && (
        <div className="mt-5 border-t border-slate-100 pt-5" data-testid="stripe-catalog-status">
          <div className="flex flex-wrap items-center gap-2 text-xs font-semibold">
            <span
              className={`rounded-full px-3 py-1 ${
                status.mode === "live"
                  ? "bg-emerald-50 text-emerald-700"
                  : "bg-amber-50 text-amber-700"
              }`}
              data-testid="stripe-mode-badge"
            >
              {status.mode === "live" ? "Live keys" : "Test keys"}
            </span>
            {status.account_id && (
              <span className="rounded-full bg-slate-100 px-3 py-1 text-slate-700">
                {status.account_id}
              </span>
            )}
            <span
              className={`rounded-full px-3 py-1 ${
                status.webhook_secret_set
                  ? "bg-emerald-50 text-emerald-700"
                  : "bg-red-50 text-red-700"
              }`}
              data-testid="stripe-webhook-badge"
            >
              {status.webhook_secret_set ? "Webhook secret set" : "Webhook secret missing"}
            </span>
          </div>
          {status.error ? (
            <p className="mt-3 text-sm text-red-600" data-testid="stripe-catalog-error">
              {status.error}
            </p>
          ) : (
            <ul className="mt-4 space-y-2">
              {status.prices.map((p) => (
                <li
                  key={p.lookup_key}
                  className="flex items-center gap-2 text-sm text-slate-700"
                  data-testid={`stripe-price-${p.lookup_key}`}
                >
                  {p.matches ? (
                    <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                  ) : (
                    <XCircle className="h-4 w-4 text-red-500" />
                  )}
                  <span className="font-semibold">{p.name}</span>
                  <span className="text-slate-500">
                    {p.exists
                      ? `$${p.amount}/mo · ${p.price_id}`
                      : `missing — expected $${p.expected_amount}/mo`}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};
